import { useEffect, useRef, useState } from 'react';

import ContentTranslations from  '@/components/elements/ContentTranslations/ContentTranslations';
import useSignalR from  '@/components/hooks/useSignalR';
import useThrottle  from  '@/components/hooks/useThrottle';
import ToastNotification, { ToastMessageType } from  '@/components/elements/global/ToastNotification';

import { IHttpRequest } from  '@/interfaces/IHttp';
import { container, TYPES } from  '@/inversifyconfig/container.config';
import { TranslatedStatus, LanguageOptionsReq, LanguageOption } from  '@/interfaces/ContentTranslations';

import '../../css/ContentTranslations.scss';

declare global {
    interface Window { UpdateLocalisationTab?: (number: number) => void}
}
interface DCTProgressMessage {
    AppGuid: string
    OrgId: number,
	ContentId: number,
    timestamp: number
}

const Content = ({orgId, contentId, appGuid}: any) => {
    const http = container.get<IHttpRequest>(TYPES.IHttpRequest); 
    const contentIdRef = useRef<number>(contentId ? contentId: 0)

    const [languages, setLanguages] = useState<LanguageOption[]>([]);
    const [message, setMessage] = useState<DCTProgressMessage>();
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastProps, setToastProps] = useState<{message?: string, type?: ToastMessageType}>({message: undefined, type: undefined })
  
    useSignalR({
        connectionString: '/hubs/clientnotifications',
        connectionMethods: [{ 
            method: 'ContentTranslationProgress', 
            newMethod: (message: DCTProgressMessage) => {
                if (Number(message.OrgId) === Number(orgId) && Number(message.ContentId) === Number(contentIdRef.current) && message.AppGuid === appGuid) {
                    setMessage({...message, timestamp: Date.now()})
                }
            }
        }],
        reconnectCallBack: () => console.log('reconnected'),
        connectionCloseCallback: () => console.log('connection closed'),
    });

    /**
     * everytime a new message is set from the socket connection, add it to the throttle queue
     * clear queue and make a new request every 2 seconds
     * run the effect everytime a new message timestamp is set
     */
    useThrottle({
        condition: !!message,
        timeout: 2000,
        action: getLanguages,
        trigger: message && message.timestamp ? message.timestamp : null
    });

    useEffect(() => {
        window.UpdateLocalisationTab = (newId) =>{
            contentIdRef.current = newId;
            getLanguages();
        }
        
        return () => {
            delete window.UpdateLocalisationTab;
        }
    }, [])

    function getLanguages(): void {
        http.get(`translate/${appGuid}/${contentIdRef.current}/language`)
        .then(({data}:{data: LanguageOptionsReq}) => {
            const sortedLanguages = data.languageOptions.toSorted((a,b) =>  a.name.localeCompare(b.name))
            setLanguages(sortedLanguages);
        })
        .catch(({data}) => {
            console.log(data);
        })
    }

    function retryLanguages(): void {
        let retryLanguages = languages
            .filter(language => language.translationStatus === TranslatedStatus.Failed)
            .map((language => ({
                localisationOrganizationId: language.localisationOrganisationId,
                enabled: language.enabled
            })));

        http.post(`translate/${appGuid}/${contentId}/language/retry`, retryLanguages)
            .then(() => getLanguages())
            .catch(() => {
                setToastProps({
                    message: '😱 Failed to retry translations - try again later',
                    type: ToastMessageType.WARNING
                });
                setShowToast(true);
            });
    }

    useEffect(() => {
        getLanguages();
    }, [])

    return (
        <section id="content-translations" data-testid="content-translations">
            <ToastNotification show={showToast} closeToast={() => setShowToast(false)} messageType={toastProps?.type}>
                {toastProps?.message}
            </ToastNotification>

            <ContentTranslations 
                languages={languages} 
                setLanguages={setLanguages} 
                retry={retryLanguages}
            />
        </ section>)
}

export default Content